// docs/116: what the inn's life is showing right now, shared by its parts: the words over heads, what each
// guest (or inn person) is doing for a moment, the dishes on the tables, the sparks, hearts and the
// stick in the air, and the beat the room claps to. Local to this client; the moments every player
// sees alike (a toast, a tale, the kitchen bell) are started from the server clock or a shared event.
import { create } from 'zustand';
import type { DishKind } from '../../critters';
import type { DrinkKind } from '../types';

/** What a guest or one of the inn's people is doing for a moment (drawn by `data-gact`). */
export type GuestAct = 'sip' | 'laugh' | 'raise' | 'clap' | 'cheer' | 'groan' | 'gasp' | 'eat' | 'listen' | 'nod' | 'pour' | 'serve' | 'wag' | 'run' | 'purr' | 'tell' | 'play';

export interface Bubble { id: number; key: string; text: string; kind: 'say' | 'tale' | 'shout' | 'song'; until: number }
export interface DishOnTable { key: string; x: number; y: number; kind: DishKind; left: 0 | 1 | 2 }
export interface Burst { id: number; kind: 'sparks' | 'hearts' | 'clink' | 'zzz' | 'notes'; x: number; y: number; until: number }
export interface Flight { id: number; kind: 'stick' | 'mug'; from: { x: number; y: number }; to: { x: number; y: number }; ms: number; arc: number; drink?: DrinkKind }
/** The beat the room claps to while the bard's tune is in its clapping bars (seconds). */
export interface Beat { period: number; delay: number; stomp: boolean }

interface LifeState {
  bubbles: Bubble[];
  acts: Record<string, GuestAct | undefined>;
  dishes: DishOnTable[];
  bursts: Burst[];
  flights: Flight[];
  beat?: Beat;
  say(key: string, text: string, ms?: number, kind?: Bubble['kind']): void;
  unsay(key: string): void;
  act(key: string, act: GuestAct | undefined, ms?: number): void;
  dish(d: DishOnTable): void;
  undish(key: string): void;
  burst(kind: Burst['kind'], x: number, y: number, ms?: number): void;
  fly(f: Omit<Flight, 'id'>): number;
  land(id: number): void;
  setBeat(b: Beat | undefined): void;
  clear(): void;
}

let ids = 0;
const timers = new Set<number>();
const later = (ms: number, fn: () => void) => { const t = window.setTimeout(() => { timers.delete(t); fn(); }, ms); timers.add(t); };

export const useLife = create<LifeState>()((set, get) => ({
  bubbles: [], acts: {}, dishes: [], bursts: [], flights: [],
  say: (key, text, ms = 2400, kind = 'say') => {
    const id = ++ids;
    set((s) => ({ bubbles: [...s.bubbles.filter((b) => b.key !== key), { id, key, text, kind, until: ms }] }));
    later(ms, () => set((s) => ({ bubbles: s.bubbles.filter((b) => b.id !== id) })));
  },
  unsay: (key) => set((s) => ({ bubbles: s.bubbles.filter((b) => b.key !== key) })),
  act: (key, act, ms) => {
    set((s) => ({ acts: { ...s.acts, [key]: act } }));
    if (ms && act) later(ms, () => { if (get().acts[key] === act) set((s) => ({ acts: { ...s.acts, [key]: undefined } })); });
  },
  dish: (d) => set((s) => ({ dishes: [...s.dishes.filter((x) => x.key !== d.key), d] })),
  undish: (key) => set((s) => ({ dishes: s.dishes.filter((x) => x.key !== key) })),
  burst: (kind, x, y, ms = 1400) => {
    const id = ++ids;
    set((s) => ({ bursts: [...s.bursts.slice(-12), { id, kind, x, y, until: ms }] }));
    later(ms, () => set((s) => ({ bursts: s.bursts.filter((b) => b.id !== id) })));
  },
  fly: (f) => { const id = ++ids; set((s) => ({ flights: [...s.flights, { ...f, id }] })); return id; },
  land: (id) => set((s) => ({ flights: s.flights.filter((f) => f.id !== id) })),
  setBeat: (beat) => { const b = get().beat; if (b?.period === beat?.period && b?.delay === beat?.delay && b?.stomp === beat?.stomp) return; set({ beat }); },
  clear: () => { for (const t of timers) window.clearTimeout(t); timers.clear(); set({ bubbles: [], acts: {}, dishes: [], bursts: [], flights: [], beat: undefined }); },
}));
